import { FormActions } from "../../components/common/FormActions";
import { BooleanTags } from "../../components/common/registerUtils";
import {
  beautify,
  createCustomAction,
  createViewAction,
  formatCurrency,
  formatDateTime,
  getForm,
  getRegister,
  hasWorkflow,
} from "../../utils/helpers";
import { URL_REVERSE_TRANSACTIONS } from "../../utils/urls";
import { useEffect } from "react";
import useSet from "../common/useSet";
import {
  API_ALL_WALLET_TRANSACTIONS,
  API_REVERSE_TRANSACTIONS,
} from "../../utils/apis";
import {
  COMPLETE_REVERSAL,
  REJECT_REVERSAL,
  REVERSE_TRANSACTION,
} from "../../utils/constants";
import { CheckCircleOutlined, DoNotDisturbAlt } from "@mui/icons-material";
import { message } from "antd";
import { IFormSection } from "../../types/common";
import { useAuth } from "../../context/AuthContext";
import useGet from "../common/useGet";

export const useReverseTransactions = () => {
  const { user } = useAuth();
  const { uploadData, success, error } = useSet();

  const handleCompleteReversal = (id: string, reversal_id: string) => {
    uploadData({
      api: `${API_REVERSE_TRANSACTIONS}${id}/complete/`,
      method: "PUT",
      params: {
        reversal_id,
      },
    });
  };

  const handleRejectReversal = (id: string) => {
    uploadData({
      api: `${API_REVERSE_TRANSACTIONS}${id}/reject/`,
      method: "PUT",
    });
  };

  useEffect(() => {
    if (success) {
      message.success(`Updated transaction reversal status`);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }

    if (Object?.keys(error)?.length > 0) {
      message.error(
        error?.detail || "Failed to update transaction reversal status",
      );
    }
  }, [success, error]);

  const tableColumns = () => [
    { id: "transactionId", header: "Transaction ID", filter: true },
    { id: "amount", header: "Amount", filter: true },
    { id: "status", header: "Status", filter: true },
    { id: "customer_tel", header: "Customer Tel", filter: true },
    { id: "partner_code", header: "Partner Code", filter: true },
    { id: "reversal_id", header: "Reversal ID", filter: true },
    { id: "reason", header: "Reason", filter: true },
    { id: "processed", header: "Processed", filter: true },
    { id: "actions", header: "Actions" },
  ];

  const tableData = (data: any) => [
    ...data?.map((item: any) => {
      return {
        ...item,
        reason: beautify(item?.reason),
        amount: formatCurrency(item?.amount),
        processed: <BooleanTags status={item?.processed} />,
        actions: (
          <FormActions
            actions={[
              ...(item?.status?.toLowerCase() === "initiated"
                ? [
                    createCustomAction(
                      `/register${URL_REVERSE_TRANSACTIONS}`,
                      REJECT_REVERSAL,
                      DoNotDisturbAlt,
                      () => handleRejectReversal(item?.id),
                    ),
                  ]
                : item?.status?.toLowerCase() === "initiated" &&
                    hasWorkflow(user?.workflows, REVERSE_TRANSACTION, 3)
                  ? [
                      createCustomAction(
                        `/register${URL_REVERSE_TRANSACTIONS}`,
                        COMPLETE_REVERSAL,
                        CheckCircleOutlined,
                        () =>
                          handleCompleteReversal(item?.id, item?.reversal_id),
                      ),
                    ]
                  : []),
              createViewAction(URL_REVERSE_TRANSACTIONS, item?.id),
            ]}
          />
        ),
      };
    }),
  ];

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }
    return {
      sections: [
        {
          title: "Reversal Details",
          fields: {
            transactionId: item?.transactionId,
            merchant_ref: item?.merchant_ref,
            amount: item?.amount,
            status: item?.status,
            send_reminder_email: (
              <BooleanTags status={item?.send_reminder_email} />
            ),
            completer_email: item?.completer_email,
            customer_tel: item?.customer_tel,
            partner_code: item?.partner_code,
            reversal_id: item?.reversal_id,
            reason: beautify(item?.reason),
            processed: <BooleanTags status={item?.processed} />,
            initiatedOn: formatDateTime(item?.initiatedOn),
            completedOn: formatDateTime(item?.completedOn),
            initaited_by: item?.initaited_by,
            completed_by: item?.completed_by,
          },
        },
      ],
    };
  };

  // const formRoute = getForm(URL_REVERSE_TRANSACTIONS);
  const formRoute = hasWorkflow(user?.workflows, REVERSE_TRANSACTION, 1)
    ? getForm(URL_REVERSE_TRANSACTIONS)
    : null;
  return { tableColumns, tableData, formRoute, viewData };
};

export const useReverseTransactionsForm = ({
  form,
  updateFormValues,
}: {
  form?: any;
  updateFormValues?: any;
}) => {
  const {
    data: transactionData,
    loading: loadingTransaction,
    error: transactionError,
    success: transactionSuccess,
    loadData: loadTransaction,
  } = useGet();

  useEffect(() => {
    if (form?.transactionId && form.transactionId.length >= 12) {
      loadTransaction({
        api: `${API_ALL_WALLET_TRANSACTIONS}?transaction_id=${form.transactionId}`,
      });
    }
  }, [form?.transactionId]);

  useEffect(() => {
    if (transactionError && Object.keys(transactionError).length > 0) {
      message.error(
        "Failed to fetch transaction details. Please check the Transaction ID.",
      );
    }
  }, [transactionError]);

  useEffect(() => {
    if (
      transactionData?.results &&
      transactionData.results.length > 0 &&
      updateFormValues
    ) {
      const transaction = transactionData.results[0];
      updateFormValues({
        amount: transaction?.amount || "",
        customer_tel: transaction?.customer_no || "",
        partner_code: transaction?.partner_code || "",
      });
    } else if (transactionSuccess && transactionData?.results?.length === 0) {
      message.warning("No transaction found with this ID.");
    }
  }, [transactionData, transactionSuccess]);

  const sections: IFormSection[] = [
    {
      title: "Reversal Details",
      fields: [
        {
          field_name: "transactionId",
          label: "Transaction ID",
          type: "text",
          props: { required: true, loading: loadingTransaction },
        },
        {
          field_name: "amount",
          label: "Amount",
          type: "number",
          props: { required: true, disabled: true },
        },
        {
          field_name: "customer_tel",
          label: "Customer Tel",
          type: "tel",
          props: { required: true, disabled: true },
        },
        {
          field_name: "partner_code",
          label: "Partner Code",
          type: "text",
          props: { required: true, disabled: true },
        },
        {
          field_name: "completer_email",
          label: "Completer Email",
          type: "text",
          props: { required: true },
        },
        {
          field_name: "reason",
          label: "Reason",
          type: "text",
          props: { required: true },
        },
      ],
    },
  ];

  const redirect = getRegister(URL_REVERSE_TRANSACTIONS);

  return { sections, redirect };
};
